import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import GaugeChart from './charts/GaugeChart';
import ClinicalIndicators from './sections/ClinicalIndicators';
import FinancialIndicators from './sections/FinancialIndicators';
import PatientDemographics from './sections/PatientDemographics';
import AppointmentIndicators from './sections/AppointmentIndicators';
import InsuranceIndicators from './sections/InsuranceIndicators';
import CaisseMobileMoney from './sections/CaisseMobileMoney';
import RecetteNette from './sections/RecetteNette';
import DateRangeFilter from '../common/DateRangeFilter';
import DrillDownModal from './DrillDownModal';
import RdvDuJour from './RdvDuJour';
import { hasAnyRole } from '../../utils/roles';
import api from '../../services/api';
import {
  Users, Calendar, TrendingUp, AlertCircle, Activity,
  BarChart2, FileText, DollarSign, UserCheck, Shield, Wallet,
  ArrowUpRight, ArrowDownRight, Stethoscope, PiggyBank,
} from 'lucide-react';

/* ── Types ─────────────────────────────────────────────── */
interface DashboardOverview {
  periode?: { date_debut: string; date_fin: string; personnalisee: boolean };
  total_active_patients: number;
  new_patients_this_month: number;
  appointments_today: number;
  appointments_this_week: number;
  appointments_periode?: number;
  revenue_this_month: number;
  collected_this_month: number;
  outstanding_invoices: number;
  consultations_this_month: number;
  expenses_this_month: number;
  tresorerie_totale: number;
  solde_caisse: number;
  solde_banques: number;
  resultat_net_month: number;
}

/* ── Helpers ────────────────────────────────────────────── */
const fmtGNF = (n: number) =>
  new Intl.NumberFormat('fr-GN', { maximumFractionDigits: 0 }).format(n);

const fmtShort = (n: number) => {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} Md`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} K`;
  return fmtGNF(n);
};

const greet = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Bonjour';
  if (h < 18) return 'Bon après-midi';
  return 'Bonsoir';
};

const TODAY = new Date().toLocaleDateString('fr-FR', {
  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
});

/** "2026-08-05" → "05/08/2026" (sans passer par Date, pas de décalage de fuseau) */
const fmtDate = (iso?: string) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

/* ── KPI Card ───────────────────────────────────────────── */
interface KpiProps {
  label: string;
  value: React.ReactNode;
  unit?: string;
  sub: string;
  icon: React.ReactNode;
  accent: string;
  delay?: number;
  metric?: string;
  onDrill?: (metric: string) => void;
}

const KpiCard: React.FC<KpiProps> = ({ label, value, unit, sub, icon, accent, delay = 0, metric, onDrill }) => (
  <div
    onClick={metric && onDrill ? () => onDrill(metric) : undefined}
    role={metric && onDrill ? 'button' : undefined}
    title={metric && onDrill ? 'Cliquer pour voir le détail' : undefined}
    className={`relative bg-white rounded-2xl overflow-hidden group ${metric && onDrill ? 'cursor-pointer hover:-translate-y-0.5 transition-transform' : 'cursor-default'}`}
    style={{
      border: '1px solid rgba(0,0,0,0.07)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
      animation: `fadeSlideUp 0.4s ease both`,
      animationDelay: `${delay}ms`,
    }}
  >
    {/* accent top strip */}
    <div className="h-[3px] w-full" style={{ background: accent }} />

    <div className="p-5">
      <div className="flex items-start justify-between mb-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400">{label}</p>
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 duration-200"
          style={{ background: `${accent}1a` }}
        >
          <div style={{ color: accent }} className="[&_svg]:w-[18px] [&_svg]:h-[18px]">{icon}</div>
        </div>
      </div>

      <div className="flex items-baseline gap-1.5">
        <span
          className="text-[2rem] font-black leading-none tabular-nums"
          style={{ color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}
        >
          {value}
        </span>
        {unit && <span className="text-sm text-gray-400 font-medium">{unit}</span>}
      </div>

      <p className="mt-2.5 text-xs text-gray-400 leading-relaxed">{sub}</p>
    </div>

    {/* hover glow */}
    <div
      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"
      style={{ background: `radial-gradient(ellipse at 70% 0%, ${accent}0d 0%, transparent 70%)` }}
    />
  </div>
);

/* ── Skeleton ───────────────────────────────────────────── */
const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-100 rounded-xl ${className}`} />
);

const OverviewSkeleton = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,0,0,0.07)' }}>
          <div className="h-[3px] w-full bg-gray-100" />
          <div className="p-5 space-y-3">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="bg-white rounded-2xl p-6" style={{ border: '1px solid rgba(0,0,0,0.07)' }}>
          <Skeleton className="h-4 w-32 mb-4" />
          <Skeleton className="h-32 w-full" />
        </div>
      ))}
    </div>
  </div>
);

/* ── Tab config ─────────────────────────────────────────── */
const TABS = [
  { id: 'overview',      label: "Vue d'ensemble", icon: BarChart2 },
  { id: 'clinical',      label: 'Clinique',        icon: Stethoscope },
  { id: 'financial',     label: 'Financier',       icon: DollarSign },
  { id: 'caisse',        label: 'Caisse & Mobile', icon: Wallet },
  { id: 'recette',       label: 'Recette nette',   icon: PiggyBank },
  { id: 'demographics',  label: 'Démographie',     icon: UserCheck },
  { id: 'appointments',  label: 'Rendez-vous',     icon: Calendar },
  { id: 'insurance',     label: 'Assurances',      icon: Shield },
];

/* ── Main Component ─────────────────────────────────────── */
export default function ModernDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState<{ start_date?: string; end_date?: string }>({});
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [drillMetric, setDrillMetric] = useState<string | null>(null);
  // remonte le DateRangeFilter (état interne non contrôlé) lors d'une réinitialisation
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    if (user && !hasAnyRole(user, ['admin', 'comptable'])) {
      navigate('/planning');
    }
  }, [user, navigate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/dashboard/analytics/overview/', {
      params: {
        ...(dateRange.start_date ? { start_date: dateRange.start_date } : {}),
        ...(dateRange.end_date ? { end_date: dateRange.end_date } : {}),
      },
    })
      .then(r => { if (!cancelled) setOverview(r.data); })
      .catch(err => { if (!cancelled) console.error(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateRange.start_date, dateRange.end_date]);

  if (user && !hasAnyRole(user, ['admin', 'comptable'])) return null;

  const periodeActive = Boolean(dateRange.start_date || dateRange.end_date);
  const collectionRate = overview && overview.revenue_this_month > 0
    ? (overview.collected_this_month / overview.revenue_this_month) * 100
    : 0;

  return (
    <>
      {/* CSS keyframes injected once */}
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>

      <div className="w-full space-y-6" style={{ animation: 'fadeIn 0.3s ease both' }}>

        {/* ── Header ──────────────────────────────────────── */}
        <div
          className="relative rounded-2xl overflow-hidden px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          style={{
            background: 'linear-gradient(135deg, #0d9488 0%, #0369a1 60%, #4f46e5 100%)',
            boxShadow: '0 4px 24px rgba(13,148,136,0.25)',
          }}
        >
          {/* noise texture overlay */}
          <div
            className="absolute inset-0 opacity-[0.03] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
              backgroundSize: '128px',
            }}
          />

          <div className="relative z-10">
            <p className="text-teal-100 text-sm font-medium capitalize">{TODAY}</p>
            <h1 className="text-2xl sm:text-3xl font-black text-white mt-0.5 tracking-tight">
              {greet()}{user?.first_name ? `, ${user.first_name}` : ''} 👋
            </h1>
            <p className="text-teal-200 text-sm mt-1">
              Cabinet Sonrisa — Tableau de bord administrateur
            </p>
            <p className="text-teal-100 text-xs mt-2 font-medium">
              Période analysée :{' '}
              {periodeActive && overview?.periode
                ? `${fmtDate(overview.periode.date_debut)} → ${fmtDate(overview.periode.date_fin)}`
                : 'mois en cours'}
              {periodeActive && (
                <button
                  onClick={() => { setDateRange({}); setResetKey(k => k + 1); }}
                  className="ml-2 underline underline-offset-2 hover:text-white"
                >
                  réinitialiser
                </button>
              )}
            </p>
          </div>

          <div className="relative z-10 flex-shrink-0">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-1 py-1">
              <DateRangeFilter key={resetKey} value={dateRange} onChange={setDateRange} />
            </div>
          </div>
        </div>

        {/* ── Tabs ────────────────────────────────────────── */}
        <div className="flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-none">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-xl text-sm font-semibold
                  transition-all duration-200 flex-shrink-0
                  ${active
                    ? 'bg-teal-600 text-white shadow-sm shadow-teal-200'
                    : 'bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50'}
                `}
                style={{ border: active ? 'none' : '1px solid rgba(0,0,0,0.08)' }}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── Content ─────────────────────────────────────── */}
        <div>
          {activeTab === 'overview' && (
            loading ? <OverviewSkeleton /> : overview ? <OverviewPanel overview={overview} collectionRate={collectionRate} onDrill={setDrillMetric} /> : null
          )}
          {activeTab === 'demographics' && periodeActive && (
            <p className="mb-3 text-xs text-gray-400">
              La démographie patients n'est pas filtrable par période (photo instantanée de la patientèle).
            </p>
          )}
          {activeTab === 'clinical'     && <ClinicalIndicators dateRange={dateRange} />}
          {activeTab === 'financial'    && <FinancialIndicators dateRange={dateRange} />}
          {activeTab === 'caisse'       && <CaisseMobileMoney dateRange={dateRange} />}
          {activeTab === 'recette'      && (
            <RecetteNette dateRange={dateRange} onPeriodeChange={setDateRange} />
          )}
          {activeTab === 'demographics' && <PatientDemographics />}
          {activeTab === 'appointments' && <AppointmentIndicators dateRange={dateRange} />}
          {activeTab === 'insurance'    && <InsuranceIndicators dateRange={dateRange} />}
        </div>
      </div>

      {drillMetric && (
        <DrillDownModal
          metric={drillMetric}
          params={{
            ...(dateRange.start_date ? { date_debut: dateRange.start_date } : {}),
            ...(dateRange.end_date ? { date_fin: dateRange.end_date } : {}),
          }}
          onClose={() => setDrillMetric(null)}
        />
      )}
    </>
  );
}

/* ── Overview panel ─────────────────────────────────────── */
function OverviewPanel({ overview, collectionRate, onDrill }: { overview: DashboardOverview; collectionRate: number; onDrill: (m: string) => void }) {
  const custom = Boolean(overview.periode?.personnalisee);
  /** Suffixe de libellé : « du mois » par défaut, « de la période » si filtre actif */
  const per = custom ? 'de la période' : 'du mois';
  const perAdj = custom ? 'sur la période' : 'ce mois';

  const kpis: KpiProps[] = [
    {
      label: 'Patients actifs',
      value: overview.total_active_patients.toLocaleString('fr-FR'),
      sub: `+${overview.new_patients_this_month} nouveaux ${perAdj}`,
      icon: <Users />,
      accent: '#0d9488',
      delay: 0,
      metric: custom ? 'nouveaux_patients' : 'patients_actifs',
    },
    custom
      ? {
          label: 'RDV sur la période',
          value: overview.appointments_periode ?? 0,
          sub: `${overview.appointments_today} aujourd'hui`,
          icon: <Calendar />,
          accent: '#0ea5e9',
          delay: 60,
          metric: 'rdv_jour',
        }
      : {
          label: "RDV aujourd'hui",
          value: overview.appointments_today,
          sub: `${overview.appointments_this_week} cette semaine`,
          icon: <Calendar />,
          accent: '#0ea5e9',
          delay: 60,
          metric: 'rdv_jour',
        },
    {
      label: `CA ${per}`,
      value: fmtShort(overview.revenue_this_month),
      unit: 'GNF',
      sub: 'Chiffre d\'affaires facturé',
      icon: <TrendingUp />,
      accent: '#6366f1',
      delay: 120,
      metric: 'ca',
    },
    {
      label: 'Encaissements',
      value: fmtShort(overview.collected_this_month),
      unit: 'GNF',
      sub: `${collectionRate.toFixed(0)}% du CA encaissé`,
      icon: <Activity />,
      accent: '#10b981',
      delay: 180,
      metric: 'encaissements',
    },
    {
      label: 'Créances',
      value: fmtShort(overview.outstanding_invoices),
      unit: 'GNF',
      sub: 'Factures en attente',
      icon: <AlertCircle />,
      accent: overview.outstanding_invoices > 5_000_000 ? '#ef4444' : '#f59e0b',
      delay: 240,
      metric: 'impayes',
    },
  ];

  const kpis2: KpiProps[] = [
    {
      label: 'Trésorerie totale',
      value: fmtShort(overview.tresorerie_totale ?? 0),
      unit: 'GNF',
      sub: `Caisse ${fmtShort(overview.solde_caisse ?? 0)} + banques ${fmtShort(overview.solde_banques ?? 0)}`,
      icon: <DollarSign />,
      accent: '#0891b2',
      delay: 0,
      metric: 'tresorerie',
    },
    {
      label: `Dépenses ${per}`,
      value: fmtShort(overview.expenses_this_month ?? 0),
      unit: 'GNF',
      sub: `Charges payées ${perAdj}`,
      icon: <ArrowDownRight />,
      accent: '#ef4444',
      delay: 60,
      metric: 'depenses',
    },
    {
      label: `Résultat net ${per}`,
      value: fmtShort(overview.resultat_net_month ?? 0),
      unit: 'GNF',
      sub: 'Produits − charges (comptabilité)',
      icon: <TrendingUp />,
      accent: (overview.resultat_net_month ?? 0) >= 0 ? '#10b981' : '#ef4444',
      delay: 120,
    },
    {
      label: 'Consultations',
      value: overview.consultations_this_month,
      sub: `Activité médicale ${per}`,
      icon: <FileText />,
      accent: '#6366f1',
      delay: 180,
      metric: 'consultations',
    },
  ];

  return (
    <div className="space-y-5">

      {/* KPI grid — 5 cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {kpis.map(k => <KpiCard key={k.label} {...k} onDrill={onDrill} />)}
      </div>

      {/* KPI grid — finance (nouveaux modules) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis2.map(k => <KpiCard key={k.label} {...k} onDrill={onDrill} />)}
      </div>

      {/* Secondary row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Gauge encaissement */}
        <div
          className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center"
          style={{
            border: '1px solid rgba(0,0,0,0.07)',
            animation: 'fadeSlideUp 0.4s ease 300ms both',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400 mb-4 self-start">
            Taux d'encaissement
          </p>
          <GaugeChart
            value={collectionRate}
            max={100}
            label="Encaissé / Facturé"
            color={collectionRate >= 80 ? 'green' : collectionRate >= 60 ? 'yellow' : 'red'}
          />
        </div>

        {/* Consultations */}
        <div
          onClick={() => onDrill('consultations')}
          title="Cliquer pour voir le détail"
          className="bg-white rounded-2xl p-6 flex flex-col justify-between cursor-pointer hover:-translate-y-0.5 transition-transform"
          style={{
            border: '1px solid rgba(0,0,0,0.07)',
            animation: 'fadeSlideUp 0.4s ease 360ms both',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400 mb-4">
            Consultations
          </p>
          <div className="flex-1 flex flex-col justify-center">
            <div className="flex items-baseline gap-2">
              <span className="text-6xl font-black tabular-nums text-indigo-600 leading-none">
                {overview.consultations_this_month}
              </span>
              <span className="text-sm text-gray-400 font-medium">{perAdj}</span>
            </div>
            <div className="mt-4 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min((overview.consultations_this_month / 100) * 100, 100)}%`,
                  background: 'linear-gradient(90deg, #6366f1, #0ea5e9)',
                }}
              />
            </div>
            <p className="mt-2 text-xs text-gray-400">
              {custom ? 'Activité médicale sur la période sélectionnée' : 'Activité médicale du mois en cours'}
            </p>
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs">
            <span className="flex items-center gap-1 font-semibold text-indigo-500 bg-indigo-50 px-2 py-1 rounded-lg">
              <FileText className="w-3 h-3" />
              {overview.consultations_this_month} consultation{overview.consultations_this_month > 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Créances détail */}
        <div
          onClick={() => onDrill('impayes')}
          title="Cliquer pour voir les factures impayées"
          className="bg-white rounded-2xl p-6 flex flex-col justify-between overflow-hidden relative cursor-pointer hover:-translate-y-0.5 transition-transform"
          style={{
            border: '1px solid rgba(0,0,0,0.07)',
            animation: 'fadeSlideUp 0.4s ease 420ms both',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
        >
          {/* Background accent circle */}
          <div
            className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full opacity-[0.07] pointer-events-none"
            style={{ background: overview.outstanding_invoices > 5_000_000 ? '#ef4444' : '#f59e0b' }}
          />

          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400 mb-4">
            Situation créances
          </p>

          <div className="flex-1 flex flex-col justify-center">
            <div className="flex items-baseline gap-1.5">
              <span
                className="text-4xl font-black tabular-nums leading-none"
                style={{ color: overview.outstanding_invoices > 5_000_000 ? '#ef4444' : '#f59e0b' }}
              >
                {fmtShort(overview.outstanding_invoices)}
              </span>
              <span className="text-sm text-gray-400">GNF</span>
            </div>
            <p className="text-xs text-gray-400 mt-2">Montant total en attente</p>
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-500">Taux recouvrement</span>
              <span className="font-bold text-gray-700">{collectionRate.toFixed(1)}%</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${collectionRate}%`,
                  background: collectionRate >= 80 ? '#10b981' : collectionRate >= 60 ? '#f59e0b' : '#ef4444',
                }}
              />
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              {overview.outstanding_invoices > 5_000_000 ? (
                <span className="flex items-center gap-1 text-red-500 font-semibold">
                  <ArrowDownRight className="w-3 h-3" /> Créances élevées
                </span>
              ) : (
                <span className="flex items-center gap-1 text-emerald-500 font-semibold">
                  <ArrowUpRight className="w-3 h-3" /> Situation saine
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Rendez-vous du jour */}
      <RdvDuJour />
    </div>
  );
}
